<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue';
import InboxesAPI from 'dashboard/api/inboxes';
import NextButton from 'dashboard/components-next/button/Button.vue';
import { useAlert } from 'dashboard/composables';

const props = defineProps({
  inbox: { type: Object, required: true },
  autoStart: { type: Boolean, default: false },
});

const qrCode = ref('');
const loading = ref(false);
const connected = ref(null); // null = ainda não consultou
const pairing = ref(false);
let pollTimer = null;
let statusTimer = null;

const isPlaceholder = computed(() => {
  return /^wuzapi\d+$/.test(props.inbox.phone_number || '');
});

const showPairing = computed(() => {
  // Mostra o pareamento/reconexão quando ainda não pareado (placeholder) ou
  // quando a sessão está desconectada (consulta ao wuzapi retornou offline).
  return pairing.value || isPlaceholder.value || connected.value === false;
});

async function startPairing() {
  if (loading.value) return;
  loading.value = true;
  pairing.value = true;
  try {
    const { data } = await InboxesAPI.wuzapiQr(props.inbox.id);
    if (data.success && data.qr_code) {
      qrCode.value = data.qr_code;
      startStatusPolling();
    } else {
      useAlert(data.error || 'Não foi possível obter o QR code.');
      pairing.value = false;
    }
  } catch (error) {
    useAlert(error.message || 'Não foi possível obter o QR code.');
    pairing.value = false;
  } finally {
    loading.value = false;
  }
}

async function checkStatus() {
  try {
    const { data } = await InboxesAPI.wuzapiStatus(props.inbox.id);
    if (data.connected) {
      connected.value = true;
      stopTimers();
    } else {
      connected.value = false;
    }
  } catch (error) {
    // continua tentando
  }
}

function startStatusPolling() {
  stopTimers();
  statusTimer = setInterval(checkStatus, 4000);
}

function stopTimers() {
  if (pollTimer) {
    clearInterval(pollTimer);
    pollTimer = null;
  }
  if (statusTimer) {
    clearInterval(statusTimer);
    statusTimer = null;
  }
}

onMounted(() => {
  // Consulta imediata + polling para detectar desconexão/reconexão.
  checkStatus();
  pollTimer = setInterval(checkStatus, 10000);
  if (props.autoStart && isPlaceholder.value) {
    startPairing();
  }
});

onUnmounted(stopTimers);
</script>

<template>
  <div v-if="connected" class="flex flex-col gap-2 items-center mb-4">
    <div
      class="flex items-center gap-2 text-n-teal-11 font-medium text-sm px-3 py-1.5 rounded-lg bg-n-teal-9/10"
    >
      <span class="inline-block size-2.5 rounded-full bg-n-teal-9" />
      Conectado
    </div>
  </div>
  <div v-else class="flex flex-col gap-3 items-center mb-4">
    <p class="text-sm text-n-slate-11 text-center">
      <template v-if="connected === false">
        A sessão do WhatsApp está desconectada. Pareie novamente escaneando o
        QR code abaixo com o aplicativo do WhatsApp (Aparelhos conectados &gt;
        Conectar aparelho).
      </template>
      <template v-else>
        Pareie seu número escaneando o QR code abaixo com o aplicativo do
        WhatsApp (Aparelhos conectados &gt; Conectar aparelho).
      </template>
    </p>
    <div
      v-if="qrCode"
      class="rounded-lg shadow outline-1 outline-n-strong outline bg-white p-2"
    >
      <img :src="qrCode" alt="QR Code WuConnect" class="rounded-lg size-56" />
    </div>
    <NextButton
      v-else-if="showPairing"
      :label="loading ? 'Conectando...' : 'Ler QR Code'"
      :is-loading="loading"
      @click="startPairing"
    />
  </div>
</template>
